import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import { Widget, toWidget } from 'ckeditor5/src/widget';
import UploadAudioCommand from './uploadaudiocommand';
import './theme/audio.css';
import icon from "./theme/icons/audio.svg";


export default class AudioUpload extends Plugin {
    static get requires() {
        return [Widget];
    }
    static get pluginName() {
        return 'AudioUpload';
    }

    init() {
        const editor = this.editor;
        const t = editor.t;
    
        // Registrar el comando personalizado
        editor.commands.add('uploadAudio', new UploadAudioCommand(editor));

        // Registrar el botón en la barra de herramientas
        editor.ui.componentFactory.add('audioUpload', locale => {
            const button = new ButtonView(locale);

            button.set({
                icon: icon,
                label: t('Upload audio'),
                tooltip: true,
                withText: false
            });

            // Ejecutar el comando al hacer clic en el botón
            button.on('execute', () => {
                editor.execute('uploadAudio');
            });

            return button;
        });

        // Configurar el esquema y las conversiones
        this._defineAudioSchema(editor);
        this._defineAudioConverters(editor);

        this._makeAudioWrapperWidget();
    }

    _makeAudioWrapperWidget() {
        const editor = this.editor;
        const t = editor.t;

        editor.editing.downcastDispatcher.on('insert:audioWrapper', (evt, data, conversionApi) => {
            const viewWriter = conversionApi.writer;
            const audioWrapper = conversionApi.mapper.toViewElement(data.item);

            // Hacer que el componente sea un widget
            toWidget(audioWrapper, viewWriter, {
                label: t('audio component')
            });
        });

        // Ajustar controles del audio después de insertar
        editor.editing.downcastDispatcher.on('insert:audio', (evt, data, conversionApi) => {
            const viewWriter = conversionApi.writer;
            const audioElement = conversionApi.mapper.toViewElement(data.item);

            if (audioElement) {
                // Usar un MutationObserver para detectar cuando se renderice
                const observer = new MutationObserver(() => {
                    const domElement = editor.editing.view.domConverter.mapViewToDom(audioElement);
                    if (domElement && domElement.tagName === 'AUDIO') {
                        domElement.controlsList = 'nodownload'; // Remover opción de descarga
                    }
                });

                observer.observe(document.body, { childList: true, subtree: true });

                setTimeout(() => observer.disconnect(), 3000); // Desconectar el observer después de 3 segundos
            }
        });
    }

    _defineAudioSchema(editor) {
        const schema = editor.model.schema;

        // Definir el contenedor <div class="ck-audio-wrapper">
        schema.register('audioWrapper', {
            allowWhere: '$block',
            allowAttributes: ['data-id'], // Permitir el atributo data-id
            isObject: true,
        });

        // Definir el elemento <audio>
        schema.register('audio', {
            isLimit: true, // Define que no puede contener hijos
            allowIn: 'audioWrapper', // Solo puede existir dentro de audioWrapper
            allowAttributes: ['src', 'data-file', 'data-id', 'data-url']
        });
    }

    _defineAudioConverters(editor) {
        const conversion = editor.conversion;

        // Convertir el modelo a la vista (downcast)
        conversion.for('downcast').elementToElement({
            model: 'audioWrapper',
            view: (modelElement, { writer }) => {
                const dataId = modelElement.getAttribute('data-id');

                // Crear el contenedor <div class="ck-audio-wrapper">
                return writer.createContainerElement('div', {
                    class: 'ck-audio-wrapper',
                    'data-id': dataId
                });
            }
        });

        conversion.for('downcast').elementToElement({
            model: 'audio',
            view: (modelElement, { writer }) => {
                const src = modelElement.getAttribute('src');
                const file = modelElement.getAttribute('data-file');
                const id = modelElement.getAttribute('data-id');
                const url = modelElement.getAttribute('data-url');

                // Crear el elemento <audio> con los atributos adicionales
                return writer.createContainerElement('audio', {
                    controls: 'controls',
                    class: 'nomediaplugin',
                    controllist: 'nodownload', // Agregar el atributo controllist
                    src,
                    'data-file': file,
                    'data-id': id,
                    'data-url': url
                });
            }
        });

        // Convertir la vista al modelo (upcast)
        conversion.for('upcast').elementToElement({
            view: {
                name: 'div',
                classes: 'ck-audio-wrapper'
            },
            model: (viewElement, { writer }) => {
                return writer.createElement('audioWrapper', {
                    'data-id': viewElement.getAttribute('data-id')
                });
            }
        });

        conversion.for('upcast').elementToElement({
            view: {
                name: 'audio',
                attributes: {
                    src: true,
                    'data-file': true,
                    'data-id': true,
                    'data-url': true
                }
            },
            model: (viewElement, { writer }) => {
                return writer.createElement('audio', {
                    src: viewElement.getAttribute('src'),
                    'data-file': viewElement.getAttribute('data-file'),
                    'data-id': viewElement.getAttribute('data-id'),
                    'data-url': viewElement.getAttribute('data-url')
                });
            }
        });
    }
}